/**
 * MobileContext
 * Provides mobile state and utility functions throughout the app.
 * 
 * CRITICAL: Mobile detection is DEVICE-BASED, not viewport-based.
 * Mobile mode is LOCKED and does NOT change on:
 * - Orientation change
 * - Viewport resize
 * - Browser chrome changes
 * 
 * Key features:
 * - isMobile: boolean indicating if DEVICE is mobile (locked, won't change on rotation)
 * - showMobileToast: function to show "Use desktop for editing" toast
 * - MobileGuard: component that blocks admin actions on mobile
 */

import React, { createContext, useContext, useCallback } from 'react';
import { useMobile } from '../hooks/useMobile';
import { toast } from 'sonner';

const MobileContext = createContext({
  isMobile: false,
  showMobileToast: () => {},
  blockMobileAction: () => false,
});

export function MobileProvider({ children }) {
  const isMobile = useMobile();

  // Show toast when user tries to perform desktop-only action on mobile
  const showMobileToast = useCallback((customMessage = null) => {
    toast.info(
      customMessage || 'Redigering görs i desktopläge för bästa upplevelse.',
      {
        duration: 3000,
        position: 'bottom-center',
      }
    );
  }, []);

  // Block action if on mobile, return true if blocked
  const blockMobileAction = useCallback((customMessage = null) => {
    if (isMobile) {
      showMobileToast(customMessage);
      return true;
    }
    return false;
  }, [isMobile, showMobileToast]);

  const value = {
    isMobile,
    showMobileToast,
    blockMobileAction,
  };

  return (
    <MobileContext.Provider value={value}>
      {children}
    </MobileContext.Provider>
  );
}

export function useMobileContext() {
  const context = useContext(MobileContext);
  if (!context) {
    throw new Error('useMobileContext must be used within a MobileProvider');
  }
  return context;
}

/**
 * MobileGuard Component
 * Renders children only on desktop, or shows fallback on mobile
 */
export function MobileGuard({ children, fallback = null, showToastOnClick = true }) {
  const { isMobile, showMobileToast } = useMobileContext();

  if (!isMobile) {
    return children;
  }

  if (fallback) {
    return fallback;
  }

  // If no fallback, render disabled version that shows toast on click
  if (showToastOnClick) {
    return (
      <div 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          showMobileToast();
        }}
        className="opacity-50 cursor-not-allowed"
      >
        {children}
      </div>
    );
  }

  return null;
}

/**
 * DesktopOnly Component
 * Only renders children on desktop, hidden on mobile
 */
export function DesktopOnly({ children }) {
  const { isMobile } = useMobileContext();
  if (isMobile) return null;
  return children;
}

/**
 * MobileOnly Component
 * Only renders children on mobile, hidden on desktop
 */
export function MobileOnly({ children }) {
  const { isMobile } = useMobileContext();
  if (!isMobile) return null;
  return children;
}

export default MobileContext;
